import Button from '@mui/material/Button';
import { useState } from 'react';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { mfaDisplayModes } from '@Components/Utilities/SandhillsConstants';

const MultiFactorAuthenticationForm = (props) => {

	const {
		MultifactorAuthenticationValues,
        displayMode,
		onMultiFactorAuthorized,
		onMultiFactorCanceled,
		forceNewMFA,
    } = props;

	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	const [multiFactorAuthTo, setMultiFactorAuthTo] = useState(null);
    const [multiFactorAuthToSource, setMultiFactorAuthToSource] = useState(null);
	const [authorizationCodeSent, setAuthorizationCodeSent] = useState(false);
	const [authorizationCodeEntered, setAuthorizationCodeEntered] = useState(null);
	const routes = useRecoilValue(atoms.routes);
	const currentSite = useRecoilValue(atoms.currentWebsite);

	const sendPasscode = () => {
		authenticationCodeRequest(routes.MFASendAuthorizationCode, { sendAuthenticationTo: multiFactorAuthTo, forceNewMFA: forceNewMFA, mfaMethodSource: multiFactorAuthToSource });
	};

	const validatePasscode = () => {
		authenticationCodeRequest(routes.MFAValidateAuthorizationCode, { authorizationCode: authorizationCodeEntered, forceNewMFA: forceNewMFA });
	};

	const authenticationCodeRequest = (mfaUrl, data) => {
		setLoading(true);
		setErrorMessage(null);

		sandhillsRequest({
			url: mfaUrl,
			method: 'post',
			data: data,
			errorHandling: {
				useConsole: false,
				useAlert: false
			}
		}).then((data) => {
			if (data) {
				let message = data?.Message;
				if (!data?.Success && !message)
					message = translator.translate('sandhills.errortryagain');

				if (message)
					setErrorMessage(message);
				else if (!data.AuthenticationID)
					setAuthorizationCodeSent(true);
				else
					onMultiFactorAuthorized(data.AuthenticationID);
			}
			else {
				setErrorMessage(translator.translate('sandhills.errortryagain'));
			}
			setLoading(false);
		});
	};

    const basicRadioField = (name, label, options) => {
        return (
			options.map((item) => (
				<div className='form-row'>
					<div className='form-element'>
						<div className='input-wrapper'>
							<label className={item.Phone && !item.CanSendTexts ? "disabled" : ""}>
								<input
									name={name}
									value={item.Phone ?? item.Email}
									type='radio'
									onChange={(e) => {
										setMultiFactorAuthTo(e.target.value);
										setMultiFactorAuthToSource(e.target.getAttribute('data-mfa-method-source'));
									}}
									disabled={item.Phone ? !item.CanSendTexts : false}
                                    data-mfa-method-source={item.Source}
								/>
								{item.Phone ?
									<>{label} {item.Phone} {!item.CanSendTexts ? `- ${translator.translate('sandhills.textscanonlybesenttomobilenumbers')}` : ""}</>
									:
									<>{label} {item.Email}</>}
							</label>
						</div>
					</div>
				</div>
			))
        );
	};

	const basicTextField = (name, label, value, onChange) => {
		return (
			<div className='form-element'>
				<div className='input-wrapper'>
					<label htmlFor={name}>{label}</label>
					<input
						id={name}
						name={name}
						type='text'
						value={value}
						onChange={onChange}
					/>
				</div>
			</div>
		);
	};

	const multiFactorBodyContents = () => {
		if (loading) {
			return (
				<LoadingRing sxBox={{ 'justify-content': 'center' }} />
			);
        }
		else if (authorizationCodeSent) {
			return (
				<>
					{translator.translate('sandhills.dspasscode.enterpasscodeinstructiontext1')}
					<span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.dspasscode.enterpasscodeinstructiontext2', [multiFactorAuthTo]) }} />
					<span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.dspasscode.enterpasscodeinstructiontext3', ["15"]) }} />
					<br /><br />

					{basicTextField('passcode', translator.translate('sandhills.dspasscode.enterpasscode'), authorizationCodeEntered, (e) => setAuthorizationCodeEntered(e.target.value))}
				</>
			)
		}
		else {
			return (
				<>
					{translator.translate('sandhills.dspasscode.contactselectinstructiontext')} <br /><br />
					<div className="multifactor-method-selection">
						{![mfaDisplayModes.BidderRegistration, mfaDisplayModes.RegistrationForm].includes(displayMode) &&
							basicRadioField('multifactorAuthTo', translator.translate('sandhills.dspasscode.sendemailto'), MultifactorAuthenticationValues.EmailAddresses)}
						{basicRadioField('multifactorAuthTo', translator.translate('sandhills.dspasscode.sendtextto'), MultifactorAuthenticationValues.PhoneNumbers)}
					</div>
					<br />
					<span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.verificationcontactus', [routes.ContactUs]) }} />
				</>
			)
		}
	};

	return (
		<div>
			<div className="multifactor-auth-container">
				<h3><strong>{translator.translate('sandhills.dspasscode.passcode')}</strong></h3>
				<br />
				{errorMessage && <div className="red-text"><strong>{errorMessage}</strong></div>}
				<br />
				{multiFactorBodyContents()}
			</div>
			{!loading && <div className="multifactor-auth-footer">
				{multiFactorAuthTo &&
					<Button variant="contained" onClick={!authorizationCodeSent ? sendPasscode : validatePasscode} className={'footer-button template-branded-button ' + (currentSite?.Site?.IsAuctionSite ? 'ts-button' : '')}>
						{translator.translate('sandhills.tradepubwebsite.mvc.submit')}
					</Button>}
				{authorizationCodeSent &&
					<Button variant="contained" onClick={sendPasscode} className={'footer-button template-branded-button ' + (currentSite?.Site?.IsAuctionSite ? 'ts-button' : '')}>
						{translator.translate('sandhills.dspasscode.resendpasscode')}
					</Button>}
				<Button className="template-branded-button footer-button" color='cancel' variant="outlined" onClick={onMultiFactorCanceled}>
					{![mfaDisplayModes.BidderRegistration, mfaDisplayModes.RegistrationForm].includes(displayMode) ? translator.translate('sandhills.cancel') : translator.translate('sandhills.otherverificationoptions')}
				</Button>
			</div>}
		</div>
	);
}

export default MultiFactorAuthenticationForm;